// Fixed-capacity pool of world-space ribbons (flight trails, dotted altitude arcs, launch streaks, intercept beams):
// one indexed draw call. Every slot is a strip of RIBBON_POINTS path samples, rewritten by the CPU when its effect
// moves; a free slot is collapsed to a point (zero-area triangles). Route sweeps use RouteTrailSystem instead.

import { BufferAttribute, BufferGeometry, DynamicDrawUsage, Mesh, Vector3, type Color, type ShaderMaterial } from 'three'
import { InvariantViolation } from '@taxwar/net'
import { EMISSIVE_LAYER, assignLayer } from '../render/layers'
import { DirtySpan, additiveMaterial } from './gpu'
import { RIBBON_FRAGMENT, RIBBON_VERTEX } from './shaders'
import type { VfxUniforms } from './uniforms'

export const RIBBON_SEGMENTS = 32
export const RIBBON_POINTS = RIBBON_SEGMENTS + 1
const SLOT_VERTICES = RIBBON_POINTS * 2
const MAX_UINT16_VERTICES = 0x10000
const VEC2 = 2
const VEC3 = 3
const VEC4 = 4
const MIN_SIDE_LENGTH = 1e-9

/** How a ribbon slot looks; all world-space sizes. */
export interface RibbonStyle {
  readonly width: number
  /** Dashes per world unit along the ribbon (0 = solid). */
  readonly dashesPerUnit: number
  readonly dashDuty: number
  /** Dash scroll speed, cycles per second (a multiple of 1/60, see SHADER_TIME_WRAP_SEC). */
  readonly scroll: number
  /** 0..1 white-hot centre line. */
  readonly core: number
}

const X_AXIS = new Vector3(1, 0, 0)
const Y_AXIS = new Vector3(0, 1, 0)

/**
 * Unit side vector across a ribbon at `here`, perpendicular to the path tangent and the local radius. A path heading
 * straight up (a beam to a strike overhead) has no surface-parallel side, so it turns to face along X/Y. Shared by
 * the pool's per-frame strips and the route-ribbon geometry built once per setup edge.
 */
export function ribbonSide(tangent: Vector3, here: Vector3, out: Vector3): Vector3 {
  out.crossVectors(tangent, here)
  if (out.lengthSq() < MIN_SIDE_LENGTH) out.crossVectors(tangent, Math.abs(tangent.x) < Math.abs(tangent.y) ? X_AXIS : Y_AXIS)
  return out.normalize()
}

export class RibbonPool {
  readonly mesh: Mesh
  private readonly geometry = new BufferGeometry()
  private readonly material: ShaderMaterial
  private readonly positions: BufferAttribute
  private readonly colours: BufferAttribute
  private readonly trails: BufferAttribute
  private readonly looks: BufferAttribute
  private readonly dirty: DirtySpan

  // Write scratch.
  private readonly here = new Vector3()
  private readonly previous = new Vector3()
  private readonly next = new Vector3()
  private readonly tangent = new Vector3()
  private readonly side = new Vector3()

  /** `hotCore` is the live white-hot colour the ribbons' cores burn toward (VfxPalette 'hot'; retunes follow). */
  constructor(
    readonly capacity: number,
    uniforms: VfxUniforms,
    hotCore: Color,
  ) {
    const vertices = capacity * SLOT_VERTICES
    if (vertices > MAX_UINT16_VERTICES) throw new InvariantViolation(`ribbon pool of ${capacity} slots exceeds 16-bit indices`)
    this.positions = new BufferAttribute(new Float32Array(vertices * VEC3), VEC3).setUsage(DynamicDrawUsage)
    this.colours = new BufferAttribute(new Float32Array(vertices * VEC4), VEC4).setUsage(DynamicDrawUsage)
    this.trails = new BufferAttribute(new Float32Array(vertices * VEC4), VEC4).setUsage(DynamicDrawUsage)
    this.looks = new BufferAttribute(new Float32Array(vertices * VEC2), VEC2).setUsage(DynamicDrawUsage)
    const indices = new Uint16Array(capacity * RIBBON_SEGMENTS * 6)
    for (let slot = 0; slot < capacity; slot++) {
      for (let segment = 0; segment < RIBBON_SEGMENTS; segment++) {
        const left = slot * SLOT_VERTICES + segment * 2
        const o = (slot * RIBBON_SEGMENTS + segment) * 6
        indices[o] = left
        indices[o + 1] = left + 1
        indices[o + 2] = left + 2
        indices[o + 3] = left + 1
        indices[o + 4] = left + 3
        indices[o + 5] = left + 2
      }
    }
    this.geometry.setAttribute('position', this.positions)
    this.geometry.setAttribute('aColor', this.colours)
    this.geometry.setAttribute('aTrail', this.trails)
    this.geometry.setAttribute('aLook', this.looks)
    this.geometry.setIndex(new BufferAttribute(indices, 1))
    this.dirty = new DirtySpan([this.positions, this.colours, this.trails, this.looks])
    this.material = additiveMaterial(RIBBON_VERTEX, RIBBON_FRAGMENT, { uTime: uniforms.uTime, uHotCore: { value: hotCore } })
    this.mesh = new Mesh(this.geometry, this.material)
    // Strips move every frame; a cached bounding sphere would cull them wrongly.
    this.mesh.frustumCulled = false
    assignLayer(this.mesh, EMISSIVE_LAYER)
  }

  /**
   * Writes a slot from RIBBON_POINTS world-space path samples (xyz triples in `points`). The strip lies across the
   * path, parallel to the globe surface, tinted colour × intensity. Brightness ramps linearly from `tailAlpha` (first
   * sample) to `headAlpha` (last); `alongStart` is the world distance of the first sample from the path start, so dashes stay put on the
   * path while the ribbon's ends move.
   */
  write(slot: number, points: Float32Array, style: RibbonStyle, colour: Color, intensity: number, tailAlpha: number, headAlpha: number, alongStart: number): void {
    this.check(slot)
    const base = slot * SLOT_VERTICES
    const r = colour.r * intensity
    const g = colour.g * intensity
    const b = colour.b * intensity
    const halfWidth = style.width / 2
    let along = alongStart
    for (let i = 0; i < RIBBON_POINTS; i++) {
      this.here.fromArray(points, i * VEC3)
      this.previous.fromArray(points, Math.max(0, i - 1) * VEC3)
      this.next.fromArray(points, Math.min(RIBBON_POINTS - 1, i + 1) * VEC3)
      if (i > 0) along += this.here.distanceTo(this.previous)
      this.tangent.subVectors(this.next, this.previous)
      ribbonSide(this.tangent, this.here, this.side).multiplyScalar(halfWidth)
      const alpha = tailAlpha + ((headAlpha - tailAlpha) * i) / RIBBON_SEGMENTS
      const left = base + i * 2
      this.positions.setXYZ(left, this.here.x - this.side.x, this.here.y - this.side.y, this.here.z - this.side.z)
      this.positions.setXYZ(left + 1, this.here.x + this.side.x, this.here.y + this.side.y, this.here.z + this.side.z)
      this.colours.setXYZW(left, r, g, b, alpha)
      this.colours.setXYZW(left + 1, r, g, b, alpha)
      this.trails.setXYZW(left, along, -1, style.dashesPerUnit, style.dashDuty)
      this.trails.setXYZW(left + 1, along, 1, style.dashesPerUnit, style.dashDuty)
      this.looks.setXY(left, style.scroll, style.core)
      this.looks.setXY(left + 1, style.scroll, style.core)
    }
    this.dirty.mark(base, SLOT_VERTICES)
  }

  hide(slot: number): void {
    this.check(slot)
    const base = slot * SLOT_VERTICES
    for (let v = 0; v < SLOT_VERTICES; v++) {
      this.positions.setXYZ(base + v, 0, 0, 0)
      this.colours.setW(base + v, 0)
    }
    this.dirty.mark(base, SLOT_VERTICES)
  }

  upload(): void {
    this.dirty.flush()
  }

  dispose(): void {
    this.mesh.removeFromParent()
    this.geometry.dispose()
    this.material.dispose()
  }

  private check(slot: number): void {
    if (slot < 0 || slot >= this.capacity) throw new InvariantViolation(`ribbon slot ${slot} outside the pool of ${this.capacity}`)
  }
}
